import type { ReactElement } from 'react'
import { useState } from 'react'

import type { MessageType } from '@skytech/ui'
import { LoaderCircle, MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import queries from '@/api/queries'
import { subscriptionServicesResources } from '@/api/subscriptionServices'
import type { SubscriptionService as SubscriptionServiceType } from '@/api/subscriptionServices'
import { Translation } from '@/components/i18n/Translation'
import { useProfile } from '@/hooks/useProfile'

import Page from '../page/Page'

import { ModifySubscriptionConfirmation } from './ModifySubscriptionConfirmation'
import { SubscriptionService } from './SubscriptionService'

import './index.styles.scss'

export const SubscriptionServices = () => {
  const { profile, isProfileLoading } = useProfile()

  const navigate = useNavigate()

  const [selectedSub, setSelectedSub] = useState<SubscriptionServiceType | null>(null)
  const [popupData, setPopupData] = useState<{
    title: ReactElement | string
    message: ReactElement | string
    type: MessageType
  }>({ title: '', message: '', type: 'error' })
  const queryClient = useQueryClient()

  const { data: subscriptionServicesList, isLoading } = useQuery(
    queries.subscriptionServicesList(),
    subscriptionServicesResources.getList
  )

  const cancelOrderMutation = useMutation(subscriptionServicesResources.cancelOrder, {
    onSuccess() {
      queryClient.invalidateQueries(queries.subscriptionServicesList(), { refetchType: 'all' })
      setSelectedSub(null)
    },
    onError() {
      setPopupData({
        type: 'error',
        title: <Translation id="Error of cancelling a subscription order" />,
        message: (
          <Translation id="We have encountered an error while cancelling this subscription order. Please refresh and try again!" />
        ),
      })
    },
  })

  if (isLoading || isProfileLoading) return <LoaderCircle size="medium" />

  if (!profile?.allowSubscriptions) {
    navigate('/')
    return null
  }

  if (subscriptionServicesList?.length === 0) {
    return (
      <div id="subscription-services">
        <div className="wrapper">
          <Translation id="Din arbeidsgiver tilbyr ingen datapakker for øyeblikket." />
        </div>
      </div>
    )
  }

  const isCancelOrderOption = selectedSub?.status === 2
  const canUpdateSubscription = profile?.canUpdateMobileSubscription !== false

  return (
    <div className="bg-white transition-all duration-200 ease-in">
      <div className="wrapper">
        {!canUpdateSubscription && (
          <div className="mb-6 rounded-md border border-yellow-200 bg-yellow-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  <Translation id="Subscription updates disabled" />
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    <Translation id="You currently cannot modify your mobile subscription. Please contact your administrator for assistance." />
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={twJoin(!canUpdateSubscription && 'pointer-events-none opacity-50')}>
          {selectedSub && !isCancelOrderOption ? (
            <div className="form-confirmation">
              <ModifySubscriptionConfirmation
                selectedSub={selectedSub}
                onOrderSuccess={() => {
                  setSelectedSub(null)
                  setPopupData({
                    title: <Translation id="Order registration successful" />,
                    message: <Translation id="Ordre har har blitt registrert." />,
                    type: 'success',
                  })
                }}
                onSelectSub={(selectedSub: SubscriptionServiceType | null) =>
                  setSelectedSub(selectedSub)
                }
              />
            </div>
          ) : (
            <Page className="subscription-services">
              <h2 className="text-2xl">
                <Translation id="Private kjøp" />
              </h2>

              {subscriptionServicesList && subscriptionServicesList.length > 0 && (
                <div className="description">
                  <p>
                    <Translation id="Dersom du ønsker å utvide dine mobiltjenester tilbyr din arbeidsgiver følgende oppgraderinger til lønnstrekk." />
                  </p>
                  <p>
                    <Translation id="Bruk gjerne kommentarfeltet dersom du har spørsmål til din forhandler." />
                  </p>
                  <br />
                  <p>
                    <b>
                      <Translation id="NB!" />
                    </b>{' '}
                    <Translation id="Oppgraderingen er ikke utført før du har fått en kvitteringsmelding på SMS om at oppgraderingen er utført. Dette varierer fra forhandler til forhandler." />
                  </p>
                </div>
              )}

              <div className="services">
                {subscriptionServicesList
                  ?.sort((a, b) => a.name.localeCompare(b.name))
                  .map(service => (
                    <SubscriptionService
                      key={service.id}
                      service={service}
                      onSelectSub={(selectedSub: SubscriptionServiceType) => {
                        if (!canUpdateSubscription) return
                        setSelectedSub(selectedSub)
                      }}
                    />
                  ))}
              </div>
            </Page>
          )}
        </div>
      </div>

      {/* Overlay message when subscription updates are disabled */}
      {!canUpdateSubscription && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 max-w-md rounded-lg bg-white p-8 shadow-xl">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                <svg
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-lg font-medium text-gray-900">
                <Translation id="Subscription updates disabled" />
              </h3>
              <p className="mb-6 text-sm text-gray-500">
                <Translation id="You currently cannot modify your mobile subscription. Please contact your administrator for assistance." />
              </p>
              <Button variant="primary" onClick={() => navigate('/')} className="w-full">
                <Translation id="Go back" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <MessagePopup
        message={popupData.message}
        title={popupData.title}
        isOpen={Boolean(popupData.message)}
        type={popupData.type}
        onClose={() => {
          setPopupData({ message: '', title: '', type: 'error' })
          setSelectedSub(null)
        }}
      />

      <MessagePopup
        isOpen={isCancelOrderOption && canUpdateSubscription}
        message={
          <Translation
            id="Are you sure you want to cancel the {orderName} order?"
            orderName={selectedSub?.name}
          />
        }
        title={<Translation id="Cancel order" />}
      >
        <footer className="flex justify-end">
          <Button variant="tertiary" onClick={() => setSelectedSub(null)}>
            <Translation id="Cancel" />
          </Button>

          <Button
            variant="primary"
            isLoading={cancelOrderMutation.isLoading}
            onClick={() => {
              if (!selectedSub?.orderId)
                throw new Error(
                  'Order id does not exist on the subscriptions you are attempting to cancel!'
                )
              cancelOrderMutation.mutate({ orderId: selectedSub.orderId })
            }}
          >
            <Translation id="Confirm" />
          </Button>
        </footer>
      </MessagePopup>
    </div>
  )
}
